import { useEffect, useState } from "react";
import { Tree } from "react-arborist";

import styles from "./Treepanel.module.css";
import circleRedWhite from "../../../assets/circleRedWhite.png";
import circleBlue from "../../../assets/circleBlue.png";
import circleGreen from "../../../assets/circleGreen.png";
import lineRed from "../../../assets/lineRed.png";
import propWhite from "../../../assets/propWhite.png";
import {
  BackgroundSound,
  FocusedSound,
  POI,
  Point,
  RouteState,
} from "../../../api/edits/editRoute.responses";
import {
  ArboristNode,
  ArboristNodeChild,
} from "../../../ts/interfaces/arborist.interfaces";

interface TreepanelProps {
  routeState: RouteState;
  stateSelected: string;
  setSelected: React.Dispatch<React.SetStateAction<string>>;
  currentLanguage: string;
}

export const Treepanel = ({
  routeState,
  stateSelected,
  setSelected,
  currentLanguage,
}: TreepanelProps) => {
  // state and effect for treeData - needed for refreshing
  const [stateTreeData, setTreeData] = useState([]);

  // effect for refreshing treeData when route changes
  useEffect(() => {
    console.log("Tree Panel effect: [props.routeState]");
    let treeData: ArboristNode[] = [];
    while (treeData.length > 0) {
      treeData.pop();
    }
    treeData.push(CreateRouteProps(routeState));
    treeData.push(CreatePois(routeState.pointsOfInterest, currentLanguage));
    treeData.push(CreateBackgroundSounds(routeState.backgroundSounds));
    treeData.push(CreateFocusedSounds(routeState.focusedSounds));
    treeData.push(CreateRouteLine(routeState.linePoints));
    setTreeData(JSON.parse(JSON.stringify(treeData)));
  }, [routeState, currentLanguage]);

  return (
    <div className={styles.container}>
      <Tree
        className={styles.treeStyle}
        data={stateTreeData}
        selection={stateSelected}
        onSelect={(nodes: any[]) =>
          setSelected(nodes[0] === undefined ? "rprop" : nodes[0].id)
        }
      >
        {Node}
      </Tree>
    </div>
  );
};

// sets react_arborist's node parameters
export function Node({ node, style, dragHandle }: any) {
  return (
    <div
      // if selected display special
      className={
        node.state.isSelected ? styles.selectedStyle : styles.unselectedStyle
      }
      style={style}
      ref={dragHandle}
    >
      {node.data.id === "rprop" ? (
        <img
          className={styles.iconRoutePropertiesStyle}
          src={propWhite}
          alt="Route Properties"
        />
      ) : (
        ""
      )}
      {node.data.id === "pois" ? (
        <img className={styles.iconPoiStyle} src={circleRedWhite} alt="POIs" />
      ) : (
        ""
      )}
      {node.data.id === "bss" ? (
        <img
          className={styles.iconBackgroundSoundStyle}
          src={circleGreen}
          alt="Background Sounds"
        />
      ) : (
        ""
      )}
      {node.data.id === "fss" ? (
        <img
          className={styles.iconFocusedSoundStyle}
          src={circleBlue}
          alt="Focused Sounds"
        />
      ) : (
        ""
      )}
      {node.data.id === "rline" ? (
        <img
          className={styles.iconRouteLineStyle}
          src={lineRed}
          alt="Route Line"
        />
      ) : (
        ""
      )}
      {node.data.name}
    </div>
  );
}

// creates new node for react_arborist tree component
// pre:   - array poi_s with poi data
// post:  - return obj with node prepared for reac_arborist
export function CreatePois(poi_s: POI[], language: string): ArboristNode {
  let obj: ArboristNode = { id: "pois", name: "POIs", children: [] };
  for (let i: number = 0; i < poi_s.length; ++i) {
    let child: ArboristNodeChild = {
      id: "pois" + poi_s[i].id,
      name:
        "#" +
        poi_s[i].pointOrder +
        ": " +
        poi_s[i].pointOfInterestTranslations[language].name,
    };
    obj.children.push(child);
  }
  return obj;
}

// creates new node for react_arborist tree component
// pre:   - array background sounds with poi data
// post:  - return obj with node prepared for reac_arborist
export function CreateBackgroundSounds(background_sounds: BackgroundSound[]) {
  let obj: ArboristNode = {
    id: "bss",
    name: "Background Sounds",
    children: [],
  };
  for (const bs of background_sounds) {
    let child: ArboristNodeChild = {
      id: "bss" + bs.id,
      name: bs.name,
    };
    obj.children.push(child);
  }
  return obj;
}

// creates new node for react_arborist tree component
// pre:   - array focused sounds with fs data
// post:  - return obj with node prepared for reac_arborist
export function CreateFocusedSounds(focused_sounds: FocusedSound[]) {
  let obj: ArboristNode = {
    id: "fss",
    name: "Focused Sounds",
    children: [],
  };
  for (const fs of focused_sounds) {
    let child: ArboristNodeChild = {
      id: "fss" + fs.id,
      name: fs.name,
    };
    obj.children.push(child);
  }
  return obj;
}

// creates new node for react_arborist tree component
// pre:   - array focused sounds with route line data
// post:  - return obj with node prepared for reac_arborist
export function CreateRouteLine(routePoints: Point[]) {
  let obj: ArboristNode = { id: "rline", name: "Route Line", children: [] };
  for (let i: number = 0; i < routePoints.length; ++i) {
    let child: ArboristNodeChild = {
      id: "rline" + String(i),
      name: "Point #" + String(i),
    };
    obj.children.push(child);
  }
  return obj;
}

// creates new prop node for react_arborist tree component
// pre:   - rotute data
// post:  - return obj with node prepared for reac_arborist
export function CreateRouteProps(routeState: RouteState) {
  let obj: ArboristNode = {
    id: "rprop",
    name: "Route Properties",
    children: [],
  };
  // let tran: ArboristNode = {
  //   id: "rpropTran",
  //   name: "Route Translations",
  //   children: [],
  // };
  // for (const [key, value] of Object.entries(routeState.routeTranslations)) {
  //   let child: ArboristNodeChild = {
  //     id: "rpropTran" + String(key),
  //     name: String(value.languageName),
  //   };
  //   tran.children.push(child);
  // }
  // obj.children.push(tran);
  let city: ArboristNodeChild = {
    id: "rpropCity",
    name: "City",
  };
  obj.children.push(city);
  let sponsor: ArboristNodeChild = {
    id: "rpropSpon",
    name: "Sponsor",
  };
  obj.children.push(sponsor);

  return obj;
}
